export type MappingRangeForm = "singleCell" | "closedRange" | "colOpen" | "rowOpen" | "twoDOpen";

export interface ParsedMappingRangeAddress {
    form: MappingRangeForm;
    canonical: string;
    startRow: number;
    startCol: number;
    endRow?: number;
    endCol?: number;
}

const CELL_PART_PATTERN = /^([A-Z]{1,3})(\d+)$/i;

function isValidColumn(column: string): boolean {
    return /^[A-Z]{1,3}$/.test(column);
}

function columnLettersToIndex(column: string): number {
    let index = 0;
    for (const char of column.toUpperCase()) {
        index = index * 26 + (char.charCodeAt(0) - 64);
    }
    return index;
}

function indexToColumnLetters(columnIndex: number): string {
    if (columnIndex <= 0) return "A";
    let index = columnIndex;
    const letters: string[] = [];
    while (index > 0) {
        index -= 1;
        letters.unshift(String.fromCharCode(65 + (index % 26)));
        index = Math.floor(index / 26);
    }
    return letters.join("");
}

function tryParseCellPart(part: string): { row: number; col: number; columnLetters: string } | null {
    const match = CELL_PART_PATTERN.exec(part.trim().replace(/\$/g, ""));
    if (!match) return null;

    const columnLetters = match[1].toUpperCase();
    const row = Number(match[2]);
    if (!isValidColumn(columnLetters) || !Number.isInteger(row) || row <= 0) return null;

    return { row, col: columnLettersToIndex(columnLetters), columnLetters };
}

function tryParseColumnOnly(part: string): { col: number; columnLetters: string } | null {
    const columnLetters = part.trim().replace(/\$/g, "").toUpperCase();
    if (!isValidColumn(columnLetters)) return null;
    return { col: columnLettersToIndex(columnLetters), columnLetters };
}

export function parseMappingRangeAddress(
    input: string
): { success: true; value: ParsedMappingRangeAddress } | { success: false; error: string } {
    const trimmed = input.trim();
    if (!trimmed) {
        return { success: false, error: "Address is required." };
    }

    let address = trimmed;
    if (address.includes("!")) {
        address = address.split("!")[1]?.trim().replace(/^'|'$/g, "") ?? "";
    }
    address = address.replace(/\$/g, "");

    if (!address.includes(":")) {
        const cell = tryParseCellPart(address);
        if (!cell) {
            return { success: false, error: "Invalid cell address." };
        }
        return {
            success: true,
            value: {
                form: "singleCell",
                canonical: `${cell.columnLetters}${cell.row}`,
                startRow: cell.row,
                startCol: cell.col,
            },
        };
    }

    const colonIndex = address.indexOf(":");
    const startPart = address.slice(0, colonIndex).trim();
    const endPart = address.slice(colonIndex + 1).trim();
    const start = tryParseCellPart(startPart);
    if (!start) {
        return { success: false, error: "Invalid range start address." };
    }

    if (endPart === "...") {
        return {
            success: true,
            value: {
                form: "twoDOpen",
                canonical: `${start.columnLetters}${start.row}:...`,
                startRow: start.row,
                startCol: start.col,
            },
        };
    }

    if (endPart.startsWith("...") && endPart.length > 3) {
        const endRow = Number(endPart.slice(3));
        if (!Number.isInteger(endRow) || endRow <= 0) {
            return { success: false, error: "Invalid row-open range." };
        }
        if (endRow !== start.row) {
            return { success: false, error: "Row-open range must repeat the anchor row." };
        }
        return {
            success: true,
            value: {
                form: "rowOpen",
                canonical: `${start.columnLetters}${start.row}:...${endRow}`,
                startRow: start.row,
                startCol: start.col,
                endRow,
            },
        };
    }

    if (endPart.endsWith("...")) {
        const colPart = endPart.slice(0, -3);
        const endCol = tryParseColumnOnly(colPart);
        if (!endCol) {
            return { success: false, error: "Invalid column-open range." };
        }
        if (endCol.col !== start.col) {
            return { success: false, error: "Column-open range must repeat the anchor column." };
        }
        return {
            success: true,
            value: {
                form: "colOpen",
                canonical: `${start.columnLetters}${start.row}:${endCol.columnLetters}...`,
                startRow: start.row,
                startCol: start.col,
                endCol: endCol.col,
            },
        };
    }

    const end = tryParseCellPart(endPart);
    if (!end) {
        return { success: false, error: "Invalid range end address." };
    }
    if (end.row < start.row || end.col < start.col) {
        return { success: false, error: "Range end must be on or after the start address." };
    }

    return {
        success: true,
        value: {
            form: "closedRange",
            canonical: `${start.columnLetters}${start.row}:${end.columnLetters}${end.row}`,
            startRow: start.row,
            startCol: start.col,
            endRow: end.row,
            endCol: end.col,
        },
    };
}

export function formatListSourceColOpenValueRange(column: string, startRow: number): string {
    const col = column.toUpperCase();
    return `${col}${startRow}:${col}...`;
}

export function tryGetTopLeftAnchor(parsed: ParsedMappingRangeAddress): string {
    return indexToColumnLetters(parsed.startCol) + parsed.startRow;
}

export function tryGetTableWriteColumn(parsed: ParsedMappingRangeAddress): string {
    return indexToColumnLetters(parsed.startCol);
}

export function columnIndexToLetters(columnIndex: number): string {
    return indexToColumnLetters(columnIndex);
}

export function buildGeographyAdjacentColumnMap(valueRange: string): Record<string, string> {
    const parsed = parseMappingRangeAddress(valueRange);
    if (!parsed.success) {
        return { code: "A", description: "B", type: "C" };
    }

    const startCol = parsed.value.startCol;
    return {
        code: indexToColumnLetters(startCol),
        description: indexToColumnLetters(startCol + 1),
        type: indexToColumnLetters(startCol + 2),
    };
}

export function normalizeListSourceValueRange(
    input: string
): { success: true; valueRange: string } | { success: false; error: string } {
    const parsed = parseMappingRangeAddress(input);
    if (!parsed.success) {
        return parsed;
    }

    if (parsed.value.form === "singleCell") {
        const col = indexToColumnLetters(parsed.value.startCol);
        return {
            success: true,
            valueRange: formatListSourceColOpenValueRange(col, parsed.value.startRow),
        };
    }

    return { success: true, valueRange: parsed.value.canonical.toUpperCase() };
}
